import type { EngFile, EngFolder } from '@/features/engineering/hooks';

export interface FolderNode {
  folder: EngFolder;
  depth: number;
  children: FolderNode[];
}

export function buildFolderTree(folders: EngFolder[]): FolderNode[] {
  const ids = new Set(folders.map((f) => f.id));
  const byParent = new Map<string | null, EngFolder[]>();
  for (const f of folders) {
    const parent = f.parent_id && ids.has(f.parent_id) ? f.parent_id : null;
    const list = byParent.get(parent) ?? [];
    list.push(f);
    byParent.set(parent, list);
  }
  const build = (parentId: string | null, depth: number): FolderNode[] =>
    (byParent.get(parentId) ?? [])
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }))
      .map((f) => ({ folder: f, depth, children: build(f.id, depth + 1) }));
  return build(null, 0);
}

export function flattenTree(nodes: FolderNode[]): FolderNode[] {
  const out: FolderNode[] = [];
  const walk = (list: FolderNode[]) => {
    for (const n of list) {
      out.push(n);
      walk(n.children);
    }
  };
  walk(nodes);
  return out;
}

/** Root → folder chain, for breadcrumbs and "path" labels. */
export function folderPath(folderId: string | null, folders: EngFolder[]): EngFolder[] {
  const byId = new Map(folders.map((f) => [f.id, f]));
  const chain: EngFolder[] = [];
  let cur = folderId ? byId.get(folderId) : undefined;
  let guard = 0;
  while (cur && guard < 50) {
    chain.unshift(cur);
    cur = cur.parent_id ? byId.get(cur.parent_id) : undefined;
    guard += 1;
  }
  return chain;
}

export function folderPathLabel(folderId: string | null, folders: EngFolder[]): string {
  const chain = folderPath(folderId, folders);
  return chain.length === 0 ? 'Drive root' : chain.map((f) => f.name).join(' / ');
}

/** Every folder id at or below `folderId` (inclusive). */
export function descendantFolderIds(folderId: string, folders: EngFolder[]): Set<string> {
  const out = new Set<string>([folderId]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const f of folders) {
      if (f.parent_id && out.has(f.parent_id) && !out.has(f.id)) {
        out.add(f.id);
        grew = true;
      }
    }
  }
  return out;
}

export function countFilesInFolder(folderId: string, files: EngFile[], folders: EngFolder[]): number {
  const ids = descendantFolderIds(folderId, folders);
  return files.filter((f) => f.folder_id && ids.has(f.folder_id)).length;
}

export function sortFiles(files: EngFile[]): EngFile[] {
  return [...files].sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' }));
}
